/**
 * THE ACTION LOG'S PURE HALF (V0.49) — the query, and how a row reads.
 *
 * DOM-free and request-free, like everything else in `lib/`, so the filter arithmetic and the
 * row wording can be tested without a server or a browser. What is NOT here is as deliberate:
 * the layout-only rule that decides whether a map flush is logged at all lives on the SERVER
 * (`routes/map.ts`), because a client-side copy would be a second opinion about what was
 * written, and the two would eventually disagree.
 */

export type AuditActorKind = 'user' | 'ai' | 'system' | 'api-key' | 'anonymous';
export type AuditOutcome = 'ok' | 'refused' | 'rejected' | 'failed';

export interface AuditRow {
  id: string;
  at: string;
  actorKind: AuditActorKind;
  actorUserId: string | null;
  actorLabel: string;
  action: string;
  outcome: AuditOutcome;
  targetType: string | null;
  targetId: string | null;
  targetLabel: string | null;
  nodeId: string | null;
  summary: string;
}

/** One entry in full — the list withholds `detail`, `GET /api/audit/:id` carries it. */
export interface AuditEntry extends AuditRow {
  detail: Record<string, unknown> | null;
}

export interface AuditVocabulary {
  areas: Array<{ key: string; label: string }>;
  actions: Array<{ key: string; label: string; area: string }>;
}

/**
 * How many rows each facet value WOULD return — counted with every other filter applied and
 * that facet's own removed, which is what makes a chip worth reading before it is clicked.
 */
export interface AuditFacets {
  outcome: Record<string, number>;
  actorKind: Record<string, number>;
  area: Record<string, number>;
}

/** The sizes the pager offers. `'all'` is capped server-side and says so when it bites. */
export type AuditPageSize = 50 | 100 | 200 | 'all';
export const PAGE_SIZES: readonly AuditPageSize[] = [50, 100, 200, 'all'];

export interface AuditPage {
  total: number;
  page: number;
  perPage: number;
  events: AuditRow[];
  /** Both ride page 0 only, so labels and counts arrive with the rows they describe. */
  vocabulary?: AuditVocabulary;
  facets?: AuditFacets;
  /** Present when `'all'` hit the server's ceiling: this many of something larger. */
  capped?: number;
}

export interface AuditFilters {
  /** Free text over the summary, the actor's name and the subject's name. */
  text: string;
  area: string;
  action: string;
  actorKind: string;
  actorUserId: string;
  outcome: string;
  /** Plain days, inclusive at both ends — the server widens `to` to the end of its day. */
  from: string;
  to: string;
  targetType: string;
  targetId: string;
  nodeId: string;
}

export const DEFAULT_AUDIT_FILTERS: Readonly<AuditFilters> = Object.freeze({
  text: '',
  area: '',
  action: '',
  actorKind: '',
  actorUserId: '',
  outcome: '',
  from: '',
  to: '',
  targetType: '',
  targetId: '',
  nodeId: '',
});

/** Is anything narrowing the view? Drives whether a "clear" control is offered at all. */
export const auditFiltersActive = (f: AuditFilters): boolean =>
  (Object.keys(DEFAULT_AUDIT_FILTERS) as Array<keyof AuditFilters>).some((k) => f[k] !== '');

/**
 * The query string for one page.
 *
 * Empty fields are OMITTED rather than sent blank: the server reads a missing parameter as "no
 * opinion", and sending `outcome=` would be a filter that matches nothing.
 */
export function auditQueryOf(f: AuditFilters, page: number, perPage?: AuditPageSize): string {
  const q = new URLSearchParams();
  if (f.text.trim()) q.set('search', f.text.trim());
  if (f.area) q.set('area', f.area);
  if (f.action) q.set('action', f.action);
  if (f.actorKind) q.set('actorKind', f.actorKind);
  if (f.actorUserId) q.set('actorUserId', f.actorUserId);
  if (f.outcome) q.set('outcome', f.outcome);
  if (f.from) q.set('from', f.from);
  if (f.to) q.set('to', f.to);
  if (f.targetType) q.set('targetType', f.targetType);
  if (f.targetId) q.set('targetId', f.targetId);
  if (f.nodeId) q.set('nodeId', f.nodeId);
  q.set('page', String(page));
  if (perPage !== undefined && perPage !== 50) q.set('perPage', String(perPage));
  return q.toString();
}

/**
 * How many pages a total divides into. `'all'` is always one page — there is nothing to
 * page through — even when the server capped what it sent, because the alternative is a
 * pager offering a second page that does not exist.
 */
export function pageCountOf(total: number, perPage: AuditPageSize): number {
  if (perPage === 'all') return 1;
  return Math.max(1, Math.ceil(total / perPage));
}

/**
 * The page numbers a pager shows: the first, the last, and a window around the current one,
 * with `null` standing for a gap. Zero-based in, zero-based out.
 *
 * A LOG GROWS WITHOUT LIMIT, so "render one button per page" is a control that works for a
 * week and then fills the screen. This keeps the count of buttons constant however large the
 * table gets, which is the whole reason the pager is not a simple map over a range.
 */
export function pageWindow(page: number, pageCount: number, span = 2): Array<number | null> {
  if (pageCount <= 1) return [0];
  const wanted = new Set<number>([0, pageCount - 1]);
  for (let p = page - span; p <= page + span; p++) {
    if (p >= 0 && p < pageCount) wanted.add(p);
  }
  const sorted = [...wanted].sort((a, b) => a - b);
  const out: Array<number | null> = [];
  let previous = -1;
  for (const p of sorted) {
    if (previous >= 0 && p - previous > 1) out.push(null);
    out.push(p);
    previous = p;
  }
  return out;
}

/** What the pager says it is showing: "1–50 of 1 284". */
export function rangeLabel(page: number, perPage: AuditPageSize, shown: number, total: number): string {
  if (!total) return 'Nothing to show';
  const first = perPage === 'all' ? 1 : page * perPage + 1;
  const last = perPage === 'all' ? shown : first + shown - 1;
  return `${first.toLocaleString()}–${last.toLocaleString()} of ${total.toLocaleString()}`;
}

/** What each outcome is called, in the reader's words rather than the database's. */
export const OUTCOME_LABEL: Record<AuditOutcome, string> = {
  ok: 'Went through',
  refused: 'Refused',
  rejected: 'Rejected',
  failed: 'Failed',
};

/** Which semantic colour a row wears. Separate from the accent, as severity always is. */
export function outcomeTone(outcome: AuditOutcome): 'good' | 'warn' | 'bad' {
  if (outcome === 'ok') return 'good';
  if (outcome === 'rejected') return 'warn';
  return 'bad';
}

export const ACTOR_KIND_LABEL: Record<AuditActorKind, string> = {
  user: 'Person',
  ai: 'The AI',
  system: 'Background job',
  'api-key': 'Widget key',
  anonymous: 'Not signed in',
};

/** Initials for the actor's avatar. '?' for somebody who was never signed in. */
export function actorInitials(row: Pick<AuditRow, 'actorKind' | 'actorLabel'>): string {
  if (row.actorKind === 'anonymous') return '?';
  const words = row.actorLabel.split(/[\s._-]+/).filter(Boolean).slice(0, 2);
  return words.map((w) => w[0]!.toUpperCase()).join('') || '?';
}

interface HttpDetail {
  method?: string;
  route?: string;
  status?: number;
  durationMs?: number;
  aborted?: boolean;
}

/**
 * What the Outcome pill says on hover.
 *
 * A row written OUTSIDE a request has no status code to show, and saying so is the honest
 * answer — those are the generation job, the verification agent and the restore's own closing
 * write, all of which happen long after whatever asked for them. The fallback names WHO wrote
 * the row from `actorKind`, which every row carries, rather than depending on a field only some
 * emitters remember to set.
 */
export function statusHint(entry: Pick<AuditEntry, 'detail' | 'actorKind'>): string {
  const http = (entry.detail?.http ?? null) as HttpDetail | null;
  if (http && typeof http.status === 'number') {
    const bits = [`HTTP ${http.status}`];
    if (http.method && http.route) bits.push(`${http.method} ${http.route}`);
    if (typeof http.durationMs === 'number') bits.push(`${http.durationMs} ms`);
    if (http.aborted) bits.push('the caller went away');
    return bits.join(' · ');
  }
  const by =
    entry.actorKind === 'ai'
      ? 'the AI pipeline when the work finished'
      : entry.actorKind === 'system'
        ? 'a background job'
        : 'the server itself';
  return `No HTTP status — recorded by ${by}`;
}

/** `detail` as ordered label/value pairs for the drawer. `http` is rendered on its own. */
export function detailPairs(detail: Record<string, unknown> | null): Array<[string, string]> {
  if (!detail) return [];
  return Object.entries(detail)
    .filter(([k]) => k !== 'http')
    .map(([k, v]) => [
      k,
      Array.isArray(v) ? v.join(', ') : v !== null && typeof v === 'object' ? JSON.stringify(v) : String(v),
    ]);
}
